import type { JsonValue } from "@protobuf-ts/runtime";
import { Family } from "../../generated/core/v1/family";
import { formatDate } from "../lib/dates";

/**
 * Reading field paths (the ValueSource format, e.g. `persons[<uuid>].registered_room.tfsa_room`)
 * out of a family, for the "we estimated X" prompts. List items are addressed by uuid, or by the
 * natural keys the engine uses (tax year, `tax_year:job_uuid`, `YYYY-MM-DD`).
 */
const SEGMENT = /^([a-z_][a-z0-9_]*)(?:\[([^\]]+)\])?$/;

function itemKey(item: Record<string, JsonValue>): string | undefined {
  if (typeof item.uuid === "string") return item.uuid;
  if (item.tax_year !== undefined && item.job_uuid !== undefined)
    return `${item.tax_year}:${item.job_uuid}`;
  if (item.tax_year !== undefined) return String(item.tax_year);
  const d = item.as_of_date as Record<string, number> | undefined;
  if (d) {
    const p = (n: number | undefined, w = 2) => String(n ?? 0).padStart(w, "0");
    return `${p(d.year, 4)}-${p(d.month)}-${p(d.day)}`;
  }
  return undefined;
}

export function familyJson(family: Family): JsonValue {
  return Family.toJson(family, { useProtoFieldName: true, emitDefaultValues: false });
}

/** The JSON value at `path`, or undefined when it (or a list item on the way) doesn't exist. */
export function valueAt(root: JsonValue, path: string): JsonValue | undefined {
  let node: JsonValue | undefined = root;
  for (const seg of path.split(".")) {
    const m = SEGMENT.exec(seg);
    if (!m || node === null || typeof node !== "object" || Array.isArray(node)) return undefined;
    const next: JsonValue | undefined = (node as Record<string, JsonValue>)[m[1]];
    if (m[2] === undefined) {
      node = next;
      continue;
    }
    if (!Array.isArray(next)) return undefined;
    node = next.find(
      (it) =>
        it !== null &&
        typeof it === "object" &&
        !Array.isArray(it) &&
        itemKey(it as Record<string, JsonValue>) === m[2],
    );
  }
  return node;
}

/**
 * A label for every field the engine's defaults pass can report as estimated (`defaulted_paths`,
 * `abi/defaults/family_defaults.py`; `paths.test.ts` reads that file and checks each has one), and
 * the fields Sharpen asks about. By field name, or by `<list or message>.<field>` where the same
 * name means something else there.
 */
const FIELD_LABEL: Record<string, string> = {
  // household
  province: "Province",
  retirement_priority_level: "How much retiring comfortably matters",
  // a person
  retirement_age: "Retirement age",
  income_replacement_percent: "Share of today’s income to retire on",
  years_in_canada_after_18: "Years in Canada since 18",
  tfsa_room: "TFSA contribution room",
  rrsp_deduction_limit: "RRSP deduction limit",
  rrsp_unused_contributions: "RRSP contributions not yet deducted",
  fhsa_participation_room: "FHSA room",
  tfsa_withdrawals_this_year: "TFSA withdrawals this year",
  // accounts
  current_value: "Balance",
  interest_apr: "Interest rate",
  adjusted_cost_base: "Adjusted cost base",
  total_contribution: "Contributed in total",
  total_contributions: "Contributed in total",
  current_year_contributed_amount: "Contributed this year",
  contribution_this_year: "Contributed this year",
  withdrawn_this_year: "Withdrawn this year",
  opened_date: "Year opened",
  creation_date: "Year opened",
  beneficiary_uuids: "Who it’s for",
  plan_type: "Personal or group RRSP",
  total_clb: "Canada Learning Bond received",
  total_cesg: "Canada Education Savings Grant received",
  cesg_unused_room_prior_years: "Unused grant room from past years",
  // income
  occupation: "Occupation",
  pre_tax_annual_income: "Pay before tax",
  start_date: "Year started",
  end_date: "Year it ends",
  payment_schedule: "Pay schedule",
  last_known_payment_date: "Last payment date",
  "jobs.last_known_payment_date": "Last pay day",
  date_schedule: "Schedule",
  // workplace pensions
  credited_service_years: "Years of credited service",
  below_ympe_accrual_rate: "Pension accrual rate",
  above_ympe_accrual_rate: "Pension accrual rate above the YMPE",
  averaging_years: "Years of pay the pension averages",
  max_benefit_percentage: "Largest pension, as a share of pay",
  employee_contribution_rate: "Your contribution rate",
  fixed_employer_contribution_rate: "Employer contribution rate",
  matched_employer_contribution_rate: "Employer matching rate",
  above_ympe_employee_contribution_rate: "Your contribution rate above the YMPE",
  above_ympe_fixed_employer_contribution_rate: "Employer contribution rate above the YMPE",
  above_ympe_matched_employer_contribution_rate: "Employer matching rate above the YMPE",
  current_balance: "Workplace plan balance",
  // what you own and owe
  "properties.current_value": "Value",
  purchase_price: "Purchase price",
  annual_property_tax: "Property tax, a year",
  purchase_date: "Year bought",
  balance: "Balance",
  interest: "Interest rate",
  credit_limit: "Credit limit",
  last_known_payment_due_date: "Last payment due date",
  min_payment_schedule: "Minimum payment schedule",
  amortization_period: "Years to pay it off",
  end_payment_date: "Year it’s paid off",
  // spending and goals
  last_known_due_date: "Last due date",
  level: "How much it matters",
  required_funding: "Cost",
  "financial_priorities.date_schedule": "When",
  down_payment: "Down payment",
  house_price: "Home price",
};

/** Words the fallback label writes in capitals ("Tfsa room" would be "TFSA room"). */
const ACRONYMS = new Set([
  "tfsa",
  "rrsp",
  "fhsa",
  "rrif",
  "resp",
  "rdsp",
  "lira",
  "dc",
  "db",
  "cpp",
  "qpp",
  "oas",
  "gis",
  "cesg",
  "clb",
  "ympe",
  "apr",
  "acb",
]);

const fieldOf = (seg: string | undefined) => seg?.replace(/\[.*\]$/, "") ?? "";

/** The label written for a path's field, if there is one (else `pathLabel` makes one up). */
export function knownPathLabel(path: string): string | undefined {
  const segs = path.split(".");
  const field = fieldOf(segs.at(-1));
  return FIELD_LABEL[`${fieldOf(segs.at(-2))}.${field}`] ?? FIELD_LABEL[field];
}

/** A short label for a path's field ("TFSA contribution room"). */
export function pathLabel(path: string): string {
  const known = knownPathLabel(path);
  if (known) return known;
  const words = fieldOf(path.split(".").at(-1))
    .split("_")
    .filter(Boolean)
    .map((w) => (ACRONYMS.has(w) ? w.toUpperCase() : w));
  return words.join(" ").replace(/^./, (c) => c.toUpperCase());
}

/** The person (by uuid) a path is about, if any. */
export function pathPerson(path: string): string | undefined {
  return /^persons\[([^\]]+)\]/.exec(path)?.[1];
}

const plural = (n: number) => (n === 1 ? "1 person" : `${n} people`);

/** "Robin", "Robin and Sky", "Robin, Sky, and Kai". */
function listText(items: string[]): string {
  if (items.length <= 2) return items.join(" and ");
  return `${items.slice(0, -1).join(", ")}, and ${items.at(-1)}`;
}

/**
 * A JSON value as words ("$12,000", "2011", "15 October 2026", "Yes"). Money-looking fields get a
 * dollar sign; a date is its year where the label asks for one ("Year started"), else in full. A
 * list of uuids (an RESP's `beneficiary_uuids`) is read as names with `nameOf`, never shown raw.
 */
/** Enum values whose generic wording ("Plan type individual") would read badly. */
const ENUM_TEXT: Record<string, string> = {
  PLAN_TYPE_INDIVIDUAL: "Personal",
  PLAN_TYPE_GROUP: "Group (through work)",
};

export function valueText(
  path: string,
  v: JsonValue | undefined,
  nameOf?: (uuid: string) => string | undefined,
): string {
  if (v === undefined || v === null) return "Not set";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") {
    // by the field's own name (a rate on an account isn't money because its path says "accounts")
    const field = fieldOf(path.split(".").at(-1));
    if (/rate|interest|apr|percent/.test(field)) return `${+v.toFixed(3)}%`;
    if (
      /room|limit|balance|value|funding|income|contribut|amount|price|tax|payment|cost|withdraw|total/.test(
        field,
      )
    )
      return `$${Math.round(v).toLocaleString("en-CA")}`;
    return String(+v.toFixed(2));
  }
  if (typeof v === "string") {
    if (v in ENUM_TEXT) return ENUM_TEXT[v];
    if (!/^[A-Z0-9_]+$/.test(v)) return v;
    const words = v.toLowerCase().split("_");
    return words.join(" ").replace(/^./, (c) => c.toUpperCase());
  }
  if (Array.isArray(v)) {
    if (!v.every((x) => typeof x === "string")) return JSON.stringify(v);
    if (!v.length) return "No one";
    const names = v.map((uuid) => nameOf?.(uuid as string));
    return names.every(Boolean) ? listText(names as string[]) : plural(v.length);
  }
  if (typeof v === "object" && "year" in v && typeof v.year === "number") {
    if (pathLabel(path).startsWith("Year")) return String(v.year);
    const { month, day } = v as { month?: number; day?: number };
    return formatDate({ year: v.year, month: month ?? 0, day: day ?? 0 });
  }
  return JSON.stringify(v);
}
